import { type ReactNode, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { serverUpdateScreensharing } from "api/api-server-actions";
import { FOLLOWING_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem } from "mds/components/Menu";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { CrossRemoveIcon, PresentPlayBackgroundIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentClassUser,
  selectCurrentSectionId,
  selectScreensharingClassUserId,
  selectUserByClassUserId,
  selectViewingScreenshare,
} from "store/selectors";
import { selectCurrentBreakoutGroupId } from "store/selectors/breakouts";
import { selectActiveClassSessionId } from "store/selectors/class-session";
import { selectCurrentClassUserIsPerformingAvCheck } from "store/selectors/class-user";
import { viewStateActions } from "store/slices/view";
import { usePresentation, usePresentationBannerText } from "utils/presentation";
import { useNavigate, useQueryParamsManager } from "utils/routing";
import { pageUrl, useViewLevel, withQueryParams } from "utils/urls";

type MenuState = {
  bannerText: string;
  buttonClassName?: string;
  actionIcon: ReactNode;
  actionText: string;
  eventName: string;
  onClick: () => void | Promise<void>;
};

// Green and orange square icon buttons are not part of the MDS, so these are one-offs.
const GREEN_BUTTON_CLASSES =
  "group-data-[dark]/themable:text-white group-data-[dark]/themable:bg-green-shade-40 group-data-[dark]/themable:border-green-shade-20 " +
  "group-data-[dark]/themable:hover:bg-green-shade-20 group-data-[dark]/themable:hover:text-white " +
  "group-data-[dark]/themable:active:bg-green-shade-20 group-data-[dark]/themable:active:border-green-shade-20 " +
  "group-data-[dark]/themable:data-[selected]:bg-green-shade-40 group-data-[dark]/themable:data-[selected]:border-green-shade-20 group-data-[dark]/themable:data-[selected]:hover:bg-green-shade-20 group-data-[dark]/themable:data-[selected]:hover:border-green-shade-20";

const ORANGE_BUTTON_CLASSES =
  "group-data-[dark]/themable:text-white group-data-[dark]/themable:bg-orange-shade-20 group-data-[dark]/themable:border-orange " +
  "group-data-[dark]/themable:hover:bg-orange group-data-[dark]/themable:hover:text-white " +
  "group-data-[dark]/themable:active:bg-orange-shade-20 group-data-[dark]/themable:active:border-orange " +
  "group-data-[dark]/themable:data-[selected]:bg-orange-shade-20 group-data-[dark]/themable:data-[selected]:border-orange group-data-[dark]/themable:data-[selected]:hover:bg-orange group-data-[dark]/themable:data-[selected]:hover:border-orange";

// For students only
export const MobilePresentationBanner = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { deleteQueryParams, setQueryParams } = useQueryParamsManager();

  const currentClassUser = useAppSelector(selectCurrentClassUser);
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const activeClassSessionId = useAppSelector(selectActiveClassSessionId);
  const currentBreakoutGroupId = useAppSelector(selectCurrentBreakoutGroupId);
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const screensharingUser = useAppSelector((state) =>
    selectUserByClassUserId(state, screensharingClassUserId),
  );
  const isViewingScreenshare = useAppSelector(selectViewingScreenshare);
  const isPerformingAvCheck = useAppSelector(selectCurrentClassUserIsPerformingAvCheck);

  const viewLevel = useViewLevel();
  const { bannerText: presentationBannerText } = usePresentationBannerText(viewLevel, false);
  const { isFollowing, isFollowingDisconnected, presentedPageId } = usePresentation();

  const screensharingUserName = screensharingUser?.first_name || "";
  const isMyScreenshare = !!(
    screensharingClassUserId && currentClassUser?.id === screensharingClassUserId
  );

  // Track whether the user has already followed the current presentation/screenshare session,
  // so we don't auto-follow them again if they explicitly stop following.
  const [hasFollowedPresentation, setHasFollowedPresentation] = useState(false);
  const [hasFollowedScreenshare, setHasFollowedScreenshare] = useState(false);

  useEffect(() => {
    if (isFollowing) setHasFollowedPresentation(true);
  }, [isFollowing]);

  useEffect(() => {
    if (!presentedPageId) setHasFollowedPresentation(false);
  }, [presentedPageId]);

  useEffect(() => {
    if (isViewingScreenshare) setHasFollowedScreenshare(true);
  }, [isViewingScreenshare]);

  useEffect(() => {
    if (!screensharingClassUserId) setHasFollowedScreenshare(false);
  }, [screensharingClassUserId]);

  // Auto-follow a screenshare when one starts (takes priority over presentation).
  useEffect(() => {
    if (
      hasFollowedScreenshare ||
      !screensharingClassUserId ||
      isMyScreenshare ||
      isViewingScreenshare
    ) {
      return;
    }
    dispatch(viewStateActions.setViewingScreenshare(true));
    setQueryParams({ [FOLLOWING_QUERY_PARAM]: "1" });
  }, [
    dispatch,
    hasFollowedScreenshare,
    isMyScreenshare,
    isViewingScreenshare,
    screensharingClassUserId,
    setQueryParams,
  ]);

  // Auto-follow a presentation when one starts (unless a screenshare is already active).
  useEffect(() => {
    if (
      hasFollowedPresentation ||
      isFollowing ||
      isPerformingAvCheck ||
      screensharingClassUserId ||
      !presentedPageId ||
      !currentSectionId
    ) {
      return;
    }
    navigate(
      withQueryParams(pageUrl(currentSectionId, presentedPageId), {
        [FOLLOWING_QUERY_PARAM]: "1",
      }),
    );
  }, [
    currentSectionId,
    hasFollowedPresentation,
    isFollowing,
    isPerformingAvCheck,
    navigate,
    presentedPageId,
    screensharingClassUserId,
  ]);

  const menuState = useMemo((): MenuState => {
    // Screenshare states take priority over presentation states
    if (isMyScreenshare) {
      return {
        bannerText: t("av.screenshare.banner.screensharing"),
        buttonClassName: GREEN_BUTTON_CLASSES,
        actionIcon: <CrossRemoveIcon />,
        actionText: t("av.screenshare.end_screenshare"),
        eventName: "Screensharing - Stop",
        onClick: async () => {
          if (!activeClassSessionId) return;
          await serverUpdateScreensharing(activeClassSessionId, null, currentBreakoutGroupId);
        },
      };
    }

    if (isViewingScreenshare) {
      return {
        bannerText: t("av.screenshare.banner.following_screenshare", {
          user: screensharingUserName,
        }),
        actionIcon: <CrossRemoveIcon />,
        actionText: t("common.stop"),
        eventName: "Screensharing - Stop following",
        onClick: () => {
          dispatch(viewStateActions.setViewingScreenshare(false));
          deleteQueryParams([FOLLOWING_QUERY_PARAM]);
        },
      };
    }

    if (screensharingClassUserId) {
      return {
        bannerText: t("av.screenshare.banner.screenshare_in_progress", {
          user: screensharingUserName,
        }),
        buttonClassName: GREEN_BUTTON_CLASSES,
        actionIcon: <PresentPlayBackgroundIcon />,
        actionText: t("main_header.start_following"),
        eventName: "Screensharing - Start following",
        onClick: () => {
          dispatch(viewStateActions.setViewingScreenshare(true));
          setQueryParams({ [FOLLOWING_QUERY_PARAM]: "1" });
        },
      };
    }

    // Presentation states
    const navigateToPresentedPage = () => {
      if (!currentSectionId || !presentedPageId) return;
      navigate(
        withQueryParams(pageUrl(currentSectionId, presentedPageId), {
          [FOLLOWING_QUERY_PARAM]: "1",
        }),
      );
    };

    if (!isFollowing) {
      return {
        bannerText: presentationBannerText,
        buttonClassName: GREEN_BUTTON_CLASSES,
        actionIcon: <PresentPlayBackgroundIcon />,
        actionText: t("main_header.follow"),
        eventName: "Presentation - Follow along",
        onClick: navigateToPresentedPage,
      };
    }

    if (isFollowingDisconnected) {
      return {
        bannerText: presentationBannerText,
        buttonClassName: ORANGE_BUTTON_CLASSES,
        actionIcon: <PresentPlayBackgroundIcon />,
        actionText: t("common.return"),
        eventName: "Presentation - Return",
        onClick: navigateToPresentedPage,
      };
    }

    return {
      bannerText: presentationBannerText,
      actionIcon: <CrossRemoveIcon />,
      actionText: t("common.stop"),
      eventName: "Presentation - Stop following",
      onClick: () => deleteQueryParams([FOLLOWING_QUERY_PARAM]),
    };
  }, [
    activeClassSessionId,
    currentBreakoutGroupId,
    currentSectionId,
    deleteQueryParams,
    dispatch,
    isFollowing,
    isFollowingDisconnected,
    isMyScreenshare,
    isViewingScreenshare,
    navigate,
    presentationBannerText,
    presentedPageId,
    screensharingClassUserId,
    screensharingUserName,
    setQueryParams,
  ]);

  if (!screensharingClassUserId && !presentedPageId) return null;

  const MenuButton = (
    <SquareIconButton
      className={twMerge(menuState.buttonClassName)}
      eventName="Mobile Presentation Banner menu opened"
      kind="tertiary"
      size="l"
      title={t("presentation.presentation_banner_label")}
      isSelected
    >
      <PresentPlayBackgroundIcon />
    </SquareIconButton>
  );

  return (
    <Menu
      className="dark-menu"
      direction="top"
      menuButton={MenuButton}
      menuClassName="xs:min-w-[320px]"
    >
      <MenuItem disabled>{menuState.bannerText}</MenuItem>
      <MenuDivider />
      <MenuItem eventName={menuState.eventName} onClick={menuState.onClick}>
        <IconText iconStart={menuState.actionIcon} text={menuState.actionText} />
      </MenuItem>
    </Menu>
  );
};
